import moment from 'moment'
import { isEqual } from 'lodash'

import { getDataSource, getMe, getPrescriptions, getPhysiciansAndEkgStackCount, getPatients, getInterpretations, getOverview, getActivity } from '../../store/appReducer'
import {
  SORT_TYPES,
  DATASOURCES,
  ALL_PATIENTS_OPTION,
  ALL_PHYSICIANS_OPTION,
  ALL_PRESCRIPTIONS_OPTION,
  PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS,
  KARDIA_PRO_DOB_START_DATE,
  KARDIA_PRO_DOB_END_DATE,
  CLEAR_STATE,
  EKG_TABLE_COLUMN_HEADERS,
} from '../../constants'
import { getNextSortDirection } from '../../containers/Tables/Utilities'
import { sortPatientIds, filterPatientIds } from '../../containers/Tables/PatientDirectoryTable/Utilities'
import { handleOnPatientClick } from '../../pages/PatientFile/module'

// ------------------------------------
// Constants
// ------------------------------------
const PREFIX = 'PATIENTS_DIRECTORY.'
export const UPDATE_REFRESH_CLICKED = `${PREFIX}UPDATE_REFRESH_CLICKED`
export const UPDATE_SELECTED_POPOVER = `${PREFIX}UPDATE_SELECTED_POPOVER`
export const UPDATE_SELECTED_INDEX = `${PREFIX}UPDATE_SELECTED_INDEX`
export const UPDATE_SORTS = `${PREFIX}UPDATE_SORTS`
export const RESET_SORTS = `${PREFIX}RESET_SORTS`
export const RESET_FILTERS = `${PREFIX}RESET_FILTERS`

export const UPDATE_SELECTED_PATIENT_ID = `${PREFIX}UPDATE_SELECTED_PATIENT_ID`
export const UPDATE_SELECTED_DATES = `${PREFIX}UPDATE_SELECTED_DATES`
export const UPDATE_SELECTED_PHYSICIAN_ID = `${PREFIX}UPDATE_SELECTED_PHYSICIAN_ID`
export const UPDATE_SELECTED_PHYSICIANS = `${PREFIX}UPDATE_SELECTED_PHYSICIANS`
export const UPDATE_SELECTED_CONNECTION_STATUS = `${PREFIX}UPDATE_SELECTED_CONNECTION_STATUS`
export const UPDATE_SELECTED_PRESCRIPTION_ID = `${PREFIX}UPDATE_SELECTED_PRESCRIPTION_ID`
export const UPDATE_SELECTED_BILLING_CODE = `${PREFIX}UPDATE_SELECTED_BILLING_CODE`
export const DATA_REQUEST = `${PREFIX}DATA_REQUEST`
export const DATA_SUCCESS = `${PREFIX}DATA_SUCCESS`
export const DATA_FAILURE = `${PREFIX}DATA_FAILURE`

// ------------------------------------
// Actions
// ------------------------------------
export const updateRefreshClicked = clicked => ({
  type: UPDATE_REFRESH_CLICKED,
  payload: clicked,
})
export const updateSelectedPopover = popover => ({
  type: UPDATE_SELECTED_POPOVER,
  payload: popover,
})
export const updateSelectedIndex = index => ({
  type: UPDATE_SELECTED_INDEX,
  payload: index,
})
export const updateSorts = (sortBy, sortDirection) => ({
  type: UPDATE_SORTS,
  payload: {
    sortBy,
    sortDirection,
  },
})
export const resetFilters = () => ({
  type: RESET_FILTERS,
})
export const resetSorts = () => ({
  type: RESET_SORTS,
})

export const updateSelectedPatientId = id => ({
  type: UPDATE_SELECTED_PATIENT_ID,
  payload: id,
})
export const updateSelectedDates = ({ startDate, endDate }) => ({
  type: UPDATE_SELECTED_DATES,
  payload: {
    startDate,
    endDate,
  },
})
export const updateSelectedPhysicianId = id => ({
  type: UPDATE_SELECTED_PHYSICIAN_ID,
  payload: id,
})
export const updateSelectedPhysicians = id => ({
  type: UPDATE_SELECTED_PHYSICIANS,
  payload: id,
})
export const updateSelectedConnectionStatus = connectionStatus => ({
  type: UPDATE_SELECTED_CONNECTION_STATUS,
  payload: connectionStatus,
})
export const updateSelectedPrescriptionId = id => ({
  type: UPDATE_SELECTED_PRESCRIPTION_ID,
  payload: id,
})
export const updateSelectedBillingCode = id => ({
  type: UPDATE_SELECTED_BILLING_CODE,
  payload: id,
})
export const dataRequest = () => ({
  type: DATA_REQUEST,
})
export const dataSuccess = () => ({
  type: DATA_SUCCESS,
})
export const dataFailure = () => ({
  type: DATA_FAILURE,
})

export const getPatientDirectoryData = (forceUpdate = false) => (dispatch, getState) => {
  dispatch(dataRequest())
  const meDataSource = getDataSource(getState(), DATASOURCES.ME)

  let promises

  if (meDataSource.data && !forceUpdate) {
    promises = Promise.resolve()
  } else {
    promises = dispatch(getMe())
  }

  return promises
    .then(() => {
      const me = getDataSource(getState(), DATASOURCES.ME)

      const {
        teams,
      } = me.data

      return dispatch(getPatients(teams[0].id))
    })
    .then(() => {
      const apiCalls = []

      const physicians = getDataSource(getState(), DATASOURCES.PHYSICIANS)
      const ekgStackCounts = getDataSource(getState(), DATASOURCES.EKG_STACK_COUNTS)
      const prescriptions = getDataSource(getState(), DATASOURCES.PRESCRIPTIONS)
      const interpretations = getDataSource(getState(), DATASOURCES.INTERPRETATIONS)
      const me = getDataSource(getState(), DATASOURCES.ME)
      const {
        teams,
      } = me.data

      if (!physicians.data || !ekgStackCounts.data || forceUpdate) {
        apiCalls.push(dispatch(getPhysiciansAndEkgStackCount(teams[0].id)))
      }

      if (!prescriptions.data || forceUpdate) {
        apiCalls.push(dispatch(getPrescriptions(teams[0].id)))
      }

      if (!interpretations.data || forceUpdate) {
        apiCalls.push(dispatch(getInterpretations(teams[0].id)))
      }
      return Promise.all(apiCalls)
    })
    .then(() => dispatch(dataSuccess()))
    .catch(err => {
      console.log('getPatientDirectoryData', err)
      dispatch(dataFailure())
    })
}


export const handleOnRefreshClick = () => (dispatch, getState) => {
  dispatch(updateRefreshClicked(true))
  return dispatch(getPatientDirectoryData(true))
    .then(() => dispatch(updateRefreshClicked(false)))
}


let previousFiltersAndSorts
let cachePatientIds = []
export const getPatientIdsFromDataSource = state => {
  const {
    [DATASOURCES.PATIENTS]: patients,
    [DATASOURCES.ENROLLMENTS]: enrollments,
  } = state.app.dataSources
  const {
    sortBy,
    sortDirection,
    selectedPatientId,
    selectedStartDate,
    selectedEndDate,
    selectedPhysicianId,
    selectedPhysicians,
    selectedConnectionStatus,
    selectedPrescriptionId,
    selectedBillingCode,
  } = state.patientDirectory

  const filtersAndSorts = {
    patientsIsFetching: patients && patients.isFetching,
    enrollmentIsFetching: enrollments && enrollments.isFetching,
    sortBy,
    sortDirection,
    selectedPatientId,
    selectedStartDate,
    selectedEndDate,
    selectedPhysicianId,
    selectedPhysicians,
    selectedConnectionStatus,
    selectedPrescriptionId,
    selectedBillingCode,
  }

  let patientIds = []
  let patientByIds = {}
  let enrollmentByIds = {}

  if (patients
    && patients.data
    && enrollments
    && enrollments.data
  ) {
    patientIds = patients.data.sortedByLastName
    patientByIds = patients.data.byId
    enrollmentByIds = enrollments.data.byId

    if (!isEqual(previousFiltersAndSorts, filtersAndSorts)) {
      patientIds = filterPatientIds(filtersAndSorts, patientIds, patientByIds, enrollmentByIds)
      patientIds = sortPatientIds(filtersAndSorts, patientIds, patientByIds, enrollmentByIds)

      cachePatientIds = patientIds
    }

    previousFiltersAndSorts = filtersAndSorts
  }

  return cachePatientIds
}

export const handleOnFilterButtonClick = columnHeader => (dispatch, getState) => {
  const {
    selectedPopover,
  } = getState().patientDirectory

  if (selectedPopover === columnHeader) {
    dispatch(updateSelectedPopover(null))
  } else {
    dispatch(updateSelectedPopover(columnHeader))
  }
}
export const handleOnFilterChange = (columnHeader, data) => (dispatch, getState) => {
  switch (columnHeader) {
  case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.PATIENT:
    dispatch(updateSelectedPatientId(data))
    dispatch(updateSelectedPopover(null))
    break
  case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.DOB:
    dispatch(updateSelectedDates(data))
    break
  // case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.ORDERING_PHYSICIAN:
  //   dispatch(updateSelectedPhysicianId(data))
  //   dispatch(updateSelectedPopover(null))
  //   break
  case EKG_TABLE_COLUMN_HEADERS.ORDERING_PHYSICIAN:
    dispatch(updateSelectedPhysicians(data))
    break
  case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.CONNECTION_STATUS:
    dispatch(updateSelectedConnectionStatus(data))
    break
  case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.PRESCRIPTION:
    dispatch(updateSelectedPrescriptionId(data))
    dispatch(updateSelectedPopover(null))
    break
  case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.BILLING_CODE:
    dispatch(updateSelectedBillingCode(data))
    break
  default:
    break
  }
}
export const handleOnSortClick = columnHeader => (dispatch, getState) => {
  const {
    sortBy,
    sortDirection,
  } = getState().patientDirectory

  if (sortBy === columnHeader) {
    const nextSortDirection = getNextSortDirection(sortDirection)
    dispatch(updateSorts(sortBy, nextSortDirection))
  } else {
    const nextSortDirection = getNextSortDirection()
    dispatch(updateSorts(columnHeader, nextSortDirection))
  }
}

export const handleOnRowClick = ({ index, rowData }) => (dispatch, getState) => {
  dispatch(handleOnPatientClick(true, true, { patientId: rowData.id }))
  dispatch(updateSelectedIndex(index))
}

export const handleOnNavClick = () => (dispatch, getState) => {
  dispatch(resetSorts())
  dispatch(resetFilters())
  dispatch(updateSelectedIndex(-1))
  dispatch(updateSelectedPopover(null))
}

const filters = Object.assign({}, {
  selectedPatientId: ALL_PATIENTS_OPTION.id,
  selectedStartDate: KARDIA_PRO_DOB_START_DATE,
  selectedEndDate: KARDIA_PRO_DOB_END_DATE,
  selectedPhysicianId: ALL_PHYSICIANS_OPTION.id,
  selectedConnectionStatus: [],
  selectedPrescriptionId: ALL_PRESCRIPTIONS_OPTION.id,
  selectedBillingCode: [],
  selectedPhysicians: [],
})
const sorts = Object.assign({}, {
  sortBy: PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.PATIENT,
  sortDirection: SORT_TYPES.ASC,
})
const initialState = {
  ...filters,
  ...sorts,
  refreshClicked: false,
  selectedIndex: -1,
  selectedPopover: null,
  dataIsFetching: false,
  dataLastUpdated: null,
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [UPDATE_REFRESH_CLICKED]: (state, action) => ({
    ...state, refreshClicked: action.payload,
  }),
  [UPDATE_SELECTED_POPOVER]: (state, action) => ({
    ...state, selectedPopover: action.payload,
  }),
  [UPDATE_SELECTED_INDEX]: (state, action) => ({
    ...state, selectedIndex: action.payload,
  }),
  [UPDATE_SORTS]: (state, action) => ({
    ...state, sortBy: action.payload.sortBy, sortDirection: action.payload.sortDirection,
  }),
  [RESET_FILTERS]: (state, action) => ({
    ...state, ...filters,
  }),
  [RESET_SORTS]: (state, action) => ({
    ...state, ...sorts,
  }),

  [UPDATE_SELECTED_PATIENT_ID]: (state, action) => ({
    ...state, selectedPatientId: action.payload,
  }),
  [UPDATE_SELECTED_DATES]: (state, action) => ({
    ...state, selectedStartDate: action.payload.startDate, selectedEndDate: action.payload.endDate,
  }),
  [UPDATE_SELECTED_PHYSICIAN_ID]: (state, action) => ({
    ...state, selectedPhysicianId: action.payload,
  }),
  [UPDATE_SELECTED_PHYSICIANS]: (state, action) => ({
    ...state, selectedPhysicians: action.payload,
  }),
  [UPDATE_SELECTED_CONNECTION_STATUS]: (state, action) => ({
    ...state, selectedConnectionStatus: action.payload,
  }),
  [UPDATE_SELECTED_PRESCRIPTION_ID]: (state, action) => ({
    ...state, selectedPrescriptionId: action.payload,
  }),
  [UPDATE_SELECTED_BILLING_CODE]: (state, action) => ({
    ...state, selectedBillingCode: action.payload,
  }),
  [DATA_REQUEST]: (state, action) => ({
    ...state, dataIsFetching: true,
  }),
  [DATA_SUCCESS]: (state, action) => ({
    ...state, dataIsFetching: false, dataLastUpdated: new Date(),
  }),
  [DATA_FAILURE]: (state, action) => ({
    ...state, dataIsFetching: false, dataLastUpdated: new Date(),
  }),
  [CLEAR_STATE]: (state, action) => ({ ...initialState }),
}

// ------------------------------------
// Reducer
// ------------------------------------
export default function patientDirectoryReducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}



// WEBPACK FOOTER //
// ./src/legacy/pages/PatientDirectory/module.js